import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { customersApi, ordersApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import Drawer from '@/components/common/Drawer'
import AddProduct from '@/components/orders/AddProduct'
import { describeMinimum, isShort, stepFor, useCategoryMinimums } from '@/lib/minimums'
import { money } from '@/lib/format'
import { estimateLine } from '@/lib/pricing'
import { unitName } from '@/lib/units'
import QuantityStepper from '@/components/common/QuantityStepper'

// The delivery date field. Set false to hide it again; the API accepts one
// either way, and it can be set or changed later on the order itself.
const SHOW_DELIVERY_DATE = true

/**
 * An order taken by hand — a phone call, a walk-in.
 *
 * Only who and what. The price comes from the customer's tier on the server,
 * exactly as it does for an order read from WhatsApp, and the finished order
 * opens straight after so it can be checked and moved on like any other.
 */
export default function NewOrder({ open, onClose }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [customer, setCustomer] = useState(null)
  const [search, setSearch] = useState('')
  const [lines, setLines] = useState([])
  // Remounts the product picker after each line, so it comes back empty
  // for the next one rather than holding the last product.
  const [pickerKey, setPickerKey] = useState(0)
  const [deliveryDate, setDeliveryDate] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const term = search.trim()
  const minimums = useCategoryMinimums()

  const { data: found, isFetching } = useQuery({
    queryKey: ['customer-search', term],
    queryFn: () => customersApi.list({ search: term, is_active: true, page_size: 8 }),
    enabled: open && !customer && term.length >= 2,
    staleTime: 60_000,
  })

  const reset = () => {
    setCustomer(null)
    setSearch('')
    setLines([])
    setDeliveryDate('')
    setNotes('')
    setError('')
  }

  const close = () => {
    reset()
    onClose()
  }

  const create = useMutation({
    mutationFn: () =>
      ordersApi.create({
        customer_id: customer.id,
        items: lines.map(({ product_id, quantity, unit }) => ({
          product_id,
          quantity: Number(quantity),
          unit,
        })),
        ...(deliveryDate ? { requested_delivery_date: deliveryDate } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      }),
    onSuccess: (order) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      reset()
      onClose()
      navigate(`/orders/${order.id}`)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  // Every line needs a quantity: the box can be cleared while editing.
  const ready =
    customer &&
    lines.length > 0 &&
    lines.every((l) => Number(l.quantity) > 0) &&
    !create.isPending

  // Priced at the chosen customer's tier, so changing the customer reprices
  // what is already listed. A preview: the server prices the saved order.
  const tier = customer?.price_tier
  const priced = lines.map((line) => ({
    ...line,
    amount: tier ? estimateLine(line.product, tier, line.quantity, line.unit).amount : null,
  }))
  const allPriced = tier && priced.length > 0 && priced.every((l) => l.amount != null)
  const total = priced.reduce((sum, l) => sum + (l.amount ?? 0), 0)

  return (
    <Drawer
      open={open}
      size="wide"
      title="New order"
      subtitle="For a phone call or a walk-in. Priced at the customer's tier."
      error={error}
      onErrorDismiss={() => setError('')}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            title={
              !customer
                ? 'Choose the customer first'
                : lines.length === 0
                  ? 'Add at least one product'
                  : undefined
            }
            onClick={() => {
              setError('')
              create.mutate()
            }}
          >
            {create.isPending ? 'Creating…' : 'Create order'}
          </button>
        </>
      }
    >
      <div className="new-order">
        <section>
          <div className="line-label">Customer</div>
          {customer ? (
            <p className="add-line-chosen">
              <strong>{customer.name}</strong>
              {customer.phone && <span className="line-code">{customer.phone}</span>}
              {customer.price_tier && <span className="line-code">Tier {customer.price_tier}</span>}
              <button type="button" className="btn-link" onClick={() => setCustomer(null)}>
                change
              </button>
            </p>
          ) : (
            <>
              <input
                autoFocus
                type="search"
                value={search}
                placeholder="Search by name, business or phone"
                onChange={(e) => setSearch(e.target.value)}
              />
              {term.length >= 2 && (
                <div className="candidate-list">
                  {isFetching && <p className="muted">Searching…</p>}
                  {!isFetching && found?.items?.length === 0 && (
                    <p className="muted">No customer matches “{term}”.</p>
                  )}
                  {found?.items?.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      className="candidate"
                      onClick={() => setCustomer(c)}
                    >
                      <span className="candidate-name">
                        {c.name}
                        {c.business_name ? ` · ${c.business_name}` : ''}
                      </span>
                      <span className="candidate-meta">{c.phone}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        <section>
          <div className="line-label">Products</div>
          {lines.length > 0 && (
            <ul className="new-order-lines">
              {priced.map((line, index) => (
                <li key={`${line.product_id}-${index}`}>
                  <span>
                    <strong>{line.name}</strong>
                    <span className="line-code">{line.code}</span>
                  </span>
                  <span className="new-order-qty">
                    <QuantityStepper
                      step={stepFor(line.product, line.unit, minimums)}
                      value={String(line.quantity)}
                      onChange={(value) =>
                        setLines((all) =>
                          all.map((l, i) => (i === index ? { ...l, quantity: value } : l))
                        )
                      }
                    />
                    {unitName(line.unit)}
                  </span>
                  <span className="new-order-amount">
                    {line.amount != null ? money(line.amount) : '—'}
                  </span>
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => setLines((all) => all.filter((_, i) => i !== index))}
                  >
                    remove
                  </button>
                  {isShort(line.product, line.quantity, line.unit, minimums) && (
                    <p className="hint caution new-order-minimum">
                      Below the minimum order of {describeMinimum(line.product, minimums)} —
                      increase it before confirming the order.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
          {allPriced && (
            <p className="new-order-total">
              <span className="muted">Estimated total, incl. GST</span>
              <strong>{money(total)}</strong>
            </p>
          )}
          <AddProduct
            key={pickerKey}
            tier={tier}
            saving={false}
            onCancel={() => setPickerKey((k) => k + 1)}
            onAdd={(payload, product) => {
              setLines((all) => [
                ...all,
                { ...payload, product, name: product.item_name, code: product.item_code },
              ])
              setPickerKey((k) => k + 1)
            }}
          />
        </section>

        <section className={SHOW_DELIVERY_DATE ? 'new-order-extra' : undefined}>
          {SHOW_DELIVERY_DATE && (
            <label className="field">
              <span>Delivery date (optional)</span>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
              />
            </label>
          )}
          <label className="field">
            <span>Notes (optional)</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
        </section>
      </div>
    </Drawer>
  )
}
